import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Calendar, User, Package, Building, Clock, ClipboardList, CheckCircle2, AlertCircle, Send, ChevronRight, LayoutDashboard, Settings as SettingsIcon, ExternalLink } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { useSettings } from '../../contexts/SettingsContext';

const CSPortalWarrantyPage: React.FC = () => {
  const { serialNo } = useParams<{ serialNo: string }>();
  const { settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [warrantyData, setWarrantyData] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimType, setClaimType] = useState<'Regular Servicing' | 'Complaint' | null>(null);
  const [complaintText, setComplaintText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatLiveTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatLiveDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  useEffect(() => {
    const fetchWarrantyData = async () => {
      try {
        setLoading(true);
        const response = await apiService.get(`/warranties/public/${serialNo}`);
        setWarrantyData(response.data.warranty);
        setServices(response.data.services || []);
      } catch (error) {
        console.error('Error fetching warranty data:', error);
        toast.error('Could not find warranty details for this serial number.');
      } finally {
        setLoading(false);
      }
    };

    if (serialNo) {
      fetchWarrantyData();
    }
  }, [serialNo]);

  const handleSubmitClaim = async () => {
    if (!claimType) {
      toast.error('Please select a claim type.');
      return;
    }

    if (claimType === 'Complaint' && !complaintText.trim()) {
      toast.error('Please describe your complaint.');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiService.post('/email/warranty-claim', {
        warranty: warrantyData,
        claimType,
        complaint: claimType === 'Complaint' ? complaintText : null
      });
      setIsSubmitted(true);
      toast.success('Your request has been submitted successfully!');
    } catch (error: any) {
      console.error('Error submitting claim:', error);
      const errorDetail = error.response?.data?.details || error.response?.data?.error || '';
      toast.error(`Failed to submit request: ${errorDetail || 'Please try again later.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!warrantyData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
            <AlertCircle size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Warranty Not Found</h1>
          <p className="text-gray-600 mb-6">We couldn't find any warranty record for serial number <span className="font-mono font-bold text-blue-600">{serialNo}</span>. Please verify the number and try again.</p>
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const daysRemaining = (endDate: string) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    const remaining = Math.floor((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return remaining;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-['DM_Sans',sans-serif]">
      {/* Dashboard Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-blue-600 p-2 rounded-xl shadow-lg shadow-blue-100">
              <ShieldCheck className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-gray-900">CS Portal</h1>
              <p className="text-[10px] uppercase font-black tracking-widest text-blue-600/60">Warranty Management</p>
            </div>
            <div className="hidden sm:flex flex-col ml-4 pl-4 border-l border-gray-200">
              <p className="text-xs font-bold text-gray-900">{formatLiveTime(currentTime)}</p>
              <p className="text-[10px] font-medium text-gray-400">{formatLiveDate(currentTime)}</p>
            </div>
            {warrantyData.brandLogo && (
              <div className="ml-4 pl-4 border-l border-gray-200 h-10 flex items-center">
                <img 
                  src={(() => {
                    const logoPath = warrantyData.brandLogo;
                    if (logoPath.startsWith('http')) return logoPath;
                    let baseUrl = process.env.REACT_APP_API_URL || '';
                    // Remove /api from the end if it exists for images
                    if (baseUrl.endsWith('/api')) {
                      baseUrl = baseUrl.slice(0, -4);
                    } else if (baseUrl.endsWith('/api/')) {
                      baseUrl = baseUrl.slice(0, -5);
                    }
                    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                    const normalizedPath = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
                    return `${normalizedBaseUrl}${normalizedPath}`;
                  })()} 
                  alt="Brand Logo" 
                  className="max-h-full max-w-[80px] object-contain" 
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/logo-placeholder.png'; // Fallback
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-6">
            <div className="sm:hidden flex flex-col items-end mr-4">
              <p className="text-[10px] font-bold text-gray-900">{formatLiveTime(currentTime)}</p>
              <p className="text-[8px] font-medium text-gray-400">{formatLiveDate(currentTime)}</p>
            </div>
            <div className="flex flex-col items-end">
              <p className="text-[10px] uppercase font-bold text-gray-400">Status</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${warrantyData.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                <span className="text-sm font-bold capitalize">{warrantyData.status}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Warranty Status Cards */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200 border border-gray-100">
              <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-6 flex items-center gap-2">
                <ShieldCheck size={16} />
                Warranty Status
              </h3>
              
              <div className="space-y-4">
                {/* General Warranty (Main) */}
                {warrantyData.warrantyYears > 0 && (
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden">
                    <div className="relative z-10">
                      <p className="text-blue-100 text-[10px] font-bold uppercase tracking-widest mb-1">General Warranty</p>
                      <div className="flex items-baseline gap-2">
                        {(() => {
                          const days = daysRemaining(warrantyData.warrantyEndDate);
                          return (
                            <>
                              <h2 className="text-3xl font-black">{Math.abs(days)}</h2>
                              <span className="text-sm font-bold text-blue-100">{days >= 0 ? 'Days Left' : 'Days Passed'}</span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <ShieldCheck size={60} />
                    </div>
                  </div>
                )}

                {/* Secondary Warranties */}
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { label: 'Service Warranty', days: daysRemaining(warrantyData.serviceWarrantyEndDate), total: warrantyData.serviceWarrantyYears, color: 'blue' },
                    { label: 'Spare Parts', days: daysRemaining(warrantyData.sparePartsWarrantyEndDate), total: warrantyData.sparePartsWarrantyYears, color: 'green' },
                    { label: 'Compressor', days: daysRemaining(warrantyData.compressorWarrantyEndDate), total: warrantyData.compressorWarrantyYears, color: 'purple' }
                  ].filter(w => w.total > 0).map((w, idx) => (
                    <div key={idx} className="p-4 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-gray-900">{w.label}</p>
                        <p className="text-[9px] text-gray-400 font-bold uppercase">{w.total} Year{w.total > 1 ? 's' : ''} Plan</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-black text-${w.days >= 0 ? w.color : 'red'}-600`}>{Math.abs(w.days)}</p>
                        <p className="text-[8px] uppercase font-black text-gray-400 tracking-tighter">{w.days >= 0 ? 'Days Left' : 'Days Passed'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Serial Number Section */}
            <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200 border border-gray-100 flex flex-col items-center text-center">
              <h4 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-1">Serial Number</h4>
              <p className="text-xl font-mono font-black text-blue-600 tracking-wider">{warrantyData.serialNo}</p>
            </div>
          </div>

          {/* Right Column - Details & Claim Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200 border border-gray-100">
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center gap-2">
                  <User size={16} />
                  Customer Info
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] uppercase font-black text-gray-400">Name</p>
                      <p className="text-sm font-bold text-gray-900">{warrantyData.customer?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-black text-gray-400">Phone</p>
                      <p className="text-sm font-bold text-gray-900">{warrantyData.customer?.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gray-400">Email Address</p>
                    <p className="text-sm font-bold text-gray-900 leading-relaxed">{warrantyData.customer?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gray-400">Customer Address</p>
                    <p className="text-sm font-bold text-gray-900 leading-relaxed">{warrantyData.customer?.address || 'N/A'}</p>
                  </div>
                  <div className="pt-2 border-t border-gray-50">
                    <p className="text-[10px] uppercase font-black text-gray-400">Project</p>
                    <p className="text-sm font-bold text-gray-900">{warrantyData.project?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gray-400">Delivery Address</p>
                    <p className="text-sm font-bold text-blue-700 leading-relaxed">{warrantyData.deliveryInvoice?.deliveryAddress || warrantyData.customer?.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200 border border-gray-100">
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center gap-2">
                  <Package size={16} />
                  Product Info
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] uppercase font-black text-gray-400">Product Name</p>
                    <p className="text-sm font-bold text-gray-900">{warrantyData.product?.name || 'N/A'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] uppercase font-black text-gray-400">Type</p>
                      <p className="text-sm font-bold text-gray-900">{warrantyData.type || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-black text-gray-400">Compressor</p>
                      <p className="text-sm font-bold text-gray-900">{warrantyData.compressorType || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gray-400">Delivery Invoice</p>
                    <p className="text-sm font-bold text-blue-600 flex items-center gap-1">
                      {warrantyData.deliveryInvoice?.invoiceNumber || 'N/A'}
                      <span className="text-gray-400 font-medium ml-1">({warrantyData.deliveryInvoice?.date ? formatDate(warrantyData.deliveryInvoice.date) : (warrantyData.deliveryInvoice?.createdAt ? formatDate(warrantyData.deliveryInvoice.createdAt) : 'N/A')})</span>
                      <ExternalLink size={12} />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service History */}
            <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200 border border-gray-100">
              <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  Service History
                </div>
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-[10px] font-black">{services.length} Records</span>
              </h3>
              
              {services.length > 0 ? (
                <div className="space-y-4">
                  {services.map((service, idx) => (
                    <div key={idx} className="relative pl-6 pb-6 border-l-2 border-gray-100 last:pb-0">
                      <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-blue-600 border-4 border-white shadow-sm"></div>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-black text-gray-900 mb-1">{service.serviceType || 'Maintenance'}</p>
                          <p className="text-xs text-gray-500 font-medium">{service.workDetails || 'Standard maintenance service performed.'}</p>
                        </div>
                        <div className="text-left md:text-right">
                          <p className="text-[10px] font-black text-blue-600 uppercase tracking-tighter">{formatDate(service.serviceDate)}</p>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Service Provider: {service.performer?.name || 'Technical Team'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                  <p className="text-sm font-bold text-gray-400">No service records found yet.</p>
                </div>
              )}
            </div>

            {/* Claim Warranty Section */}
            {!isSubmitted ? (
              <div className="bg-white rounded-3xl p-8 shadow-xl shadow-blue-100 border border-blue-100 overflow-hidden relative">
                {!showClaimForm ? (
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-xl font-black text-gray-900 mb-2">Need Assistance?</h3>
                      <p className="text-sm text-gray-500 font-medium">If you're experiencing issues or need regular maintenance, submit a warranty claim request directly to our support team.</p>
                    </div>
                    <button 
                      onClick={() => setShowClaimForm(true)}
                      className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 flex items-center gap-2 group"
                    >
                      Claim Warranty
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-black text-gray-900">Submit Warranty Request</h3>
                      <button onClick={() => setShowClaimForm(false)} className="text-xs font-bold text-gray-400 hover:text-gray-600 uppercase tracking-widest">Cancel</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button 
                        onClick={() => setClaimType('Regular Servicing')}
                        className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${claimType === 'Regular Servicing' ? 'border-blue-600 bg-blue-50' : 'border-gray-100 hover:border-blue-200'}`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${claimType === 'Regular Servicing' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'}`}>
                          {claimType === 'Regular Servicing' && <CheckCircle2 size={14} />}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-black text-gray-900">Regular Servicing</p>
                          <p className="text-[10px] text-gray-500 font-medium">Routine maintenance check</p>
                        </div>
                      </button>

                      <button 
                        onClick={() => setClaimType('Complaint')}
                        className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${claimType === 'Complaint' ? 'border-red-600 bg-red-50' : 'border-gray-100 hover:border-red-200'}`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${claimType === 'Complaint' ? 'border-red-600 bg-red-600 text-white' : 'border-gray-300'}`}>
                          {claimType === 'Complaint' && <CheckCircle2 size={14} />}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-black text-gray-900">Complaint</p>
                          <p className="text-[10px] text-gray-500 font-medium">Report a product issue</p>
                        </div>
                      </button>
                    </div>

                    <AnimatePresence>
                      {claimType === 'Complaint' && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <label className="block text-[10px] uppercase font-black text-gray-400 mb-2">Describe Complaint</label>
                          <textarea 
                            value={complaintText}
                            onChange={(e) => setComplaintText(e.target.value)}
                            placeholder="Please provide details about the issue you're facing..."
                            className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 text-sm font-medium focus:border-red-300 focus:outline-none transition-colors min-h-[120px]"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <button 
                      onClick={handleSubmitClaim}
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <LoadingSpinner size="sm" color="surface" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Submit Warranty Claim Request
                        </>
                      )}
                    </button>
                  </motion.div>
                )}
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-600 rounded-3xl p-10 text-white text-center shadow-2xl shadow-green-100"
              >
                <div className="bg-white/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 size={40} />
                </div>
                <h3 className="text-2xl font-black mb-2">Request Submitted!</h3>
                <p className="text-green-100 font-medium mb-8">We've received your warranty claim request. Our support team will review the details and a representative will communicate with you shortly.</p>
                <div className="bg-white/10 p-4 rounded-2xl inline-block text-xs font-bold">
                  Reference: {serialNo}-{new Date().getTime().toString().slice(-6)}
                </div>
              </motion.div>
            )}

            {/* Warranty Terms & Conditions */}
            <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200 border border-gray-100">
              <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-6 flex items-center gap-2">
                <ClipboardList size={16} />
                Warranty Terms & Conditions
              </h3>
              <div className="space-y-4">
                <ul className="space-y-3">
                  {[
                    "The warranty is void if the unit is serviced, repaired, modified, or relocated by anyone other than our authorized technicians or agencies.",
                    "The warranty does not cover damage from power surges, improper wiring, physical impact, pest or vermin infestation, or natural disasters.",
                    "The warranty excludes failures caused by operating beyond the intended capacity, heavy commercial use, or lack of routine maintenance (e.g., clogged filters).",
                    "External accessories (remotes, batteries, plastics, piping kits) are excluded from the warranty. Claims require the original warranty card and an unaltered serial number."
                  ].map((term, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-gray-600">
                      <span className="font-black text-blue-600 flex-shrink-0">{i + 1}.</span>
                      <span>{term}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="max-w-7xl mx-auto px-4 py-12 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
            <Building size={24} className="text-gray-400" />
          </div>
          <p className="text-xs font-black uppercase tracking-widest text-gray-400">© {new Date().getFullYear()} THE AIRCONS LTD. ALL RIGHTS RESERVED.</p>
          <div className="flex gap-6 text-[10px] font-black uppercase tracking-widest text-blue-600/50">
            <a href="#" className="hover:text-blue-600 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-blue-600 transition-colors">Terms of Service</a>
            <a href="mailto:support@theaircons.com" className="hover:text-blue-600 transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CSPortalWarrantyPage;
